import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Cross } from '@saleshandy/icons';
import { RootState } from '../../../../store/root-reducer';
import LeadFinderExtensionModal from '../../../home/components/header/components/lead-finder-extension-modal';
import { ensureLeadFinderExtensionAccess } from '../../../home/components/header/components/lead-finder-extension-modal/demo-access-guard';
import {
  LEAD_FINDER_EXTENSION_BANNER_DISMISSED_EVENT,
  dismissLeadFinderExtensionBanner,
  isLeadFinderExtensionBannerDismissed,
} from './banner-dismissal';

// Slim, dismissible install nudge shown at the top of the Lead Finder (v2) page.
// Opens the install wizard on "Install now", and remembers dismissal/install
// against the current login (token) so it reappears on the next fresh login.
const LeadFinderExtensionBanner: React.FC = () => {
  const creditGiven = useSelector((state: RootState) => state.home.creditGiven);
  const [dismissed, setDismissed] = useState<boolean>(
    isLeadFinderExtensionBannerDismissed,
  );
  const [showModal, setShowModal] = useState(false);

  // Hide immediately when the install wizard is opened from anywhere else
  // (empty-state link, header menu) during this login.
  useEffect(() => {
    const handleDismissed = () => setDismissed(true);
    window.addEventListener(
      LEAD_FINDER_EXTENSION_BANNER_DISMISSED_EVENT,
      handleDismissed,
    );
    return () =>
      window.removeEventListener(
        LEAD_FINDER_EXTENSION_BANNER_DISMISSED_EVENT,
        handleDismissed,
      );
  }, []);

  const handleInstallNow = () => {
    if (!ensureLeadFinderExtensionAccess()) {
      return;
    }
    // Clicking install also suppresses the banner for this login.
    dismissLeadFinderExtensionBanner();
    setShowModal(true);
  };

  // Hidden once credits are claimed, or after the user dismissed/installed
  // during the current login (until they log in again).
  const showBanner = !creditGiven && !dismissed;

  return (
    <>
      {showBanner && (
        <div
          className="bs-lf-ext-banner"
          role="region"
          aria-label="Lead Finder Chrome extension"
        >
          <div className="bs-lf-ext-banner__content">
            <p className="bs-lf-ext-banner__text mb-0">
              <span className="semibold-2 gray-txt-9">
                New Saleshandy - LinkedIn Chrome Extension is here.
              </span>{' '}
              <span className="regular-2 gray-txt-11">
                Reveal leads &amp; enrich companies on LinkedIn. Get 50 free
                credits on install.
              </span>
            </p>

            <button
              type="button"
              className="bs-lf-ext-banner__cta"
              onClick={handleInstallNow}
            >
              Install now
            </button>
          </div>

          <button
            type="button"
            className="bs-lf-ext-banner__close"
            aria-label="Dismiss"
            onClick={dismissLeadFinderExtensionBanner}
          >
            <Cross width={18} height={18} />
          </button>
        </div>
      )}

      <LeadFinderExtensionModal
        show={showModal}
        onClose={() => setShowModal(false)}
        source="banner"
      />
    </>
  );
};

export default LeadFinderExtensionBanner;
